import React, { useEffect, useState } from "react";
import { EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const CATEGORY_LABELS = {
  nickname: "Nickname",
  business_suffix: "Business suffix",
  all_caps: "ALL-CAPS",
  designation_tail: "Designations",
  punct: "Stray Punctuation in Name",
};

function formatLabel(it) {
  if (it.kind === "name_fix") return `${it.label} · ${CATEGORY_LABELS[it.category] || it.category}`;
  return it.label;
}

// Collapsed "N ignored · show" tray listing team-wide ignores for one section.
// `version` bumps re-fetch after an ignore / undo elsewhere in the section.
export default function IgnoredTray({ kind, version, onRestored }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(`/cleanup/ignores`, { params: { kind } });
      setItems(data.items || []);
    } catch (e) { /* tray is non-critical */ }
  }
  useEffect(() => { load(); }, [kind, version]); // eslint-disable-line react-hooks/exhaustive-deps

  async function restore(it) {
    setBusyId(it.id);
    try {
      await api.delete(`/cleanup/ignores/${it.id}`);
      toast.success(`Restored ${formatLabel(it)}`, { description: "It will show again on the next audit." });
      setItems((xs) => xs.filter((x) => x.id !== it.id));
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setBusyId(null); }
  }

  if (items.length === 0) return null;

  return (
    <div className="pt-3 border-t border-slate-200" data-testid={`ignored-tray-${kind}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors"
        data-testid={`ignored-tray-toggle-${kind}`}
      >
        <EyeOff className="h-3.5 w-3.5" />
        {items.length} ignored · {open ? "hide" : "show"}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`ignored-tray-list-${kind}`}>
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-md border border-slate-200 bg-slate-50 p-2.5 flex items-center justify-between gap-3 text-slate-500"
              data-testid={`ignored-item-${it.id}`}
            >
              <div className="min-w-0">
                <div className={`text-sm font-medium text-slate-600 truncate ${it.kind === "name_group" ? "capitalize" : ""}`}>
                  {formatLabel(it)}
                </div>
                {it.detail && <div className="text-xs truncate">{it.detail}</div>}
                <div className="text-xs">
                  Ignored by {it.ignored_by} · {new Date(it.ignored_at).toLocaleString()}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => restore(it)}
                disabled={busyId === it.id}
                className="shrink-0"
                data-testid={`ignored-restore-${it.id}`}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
